import { useEffect, useState } from 'react'
import { Button, HStack, Heading, Spinner, Text, VStack } from '@chakra-ui/react'
import { FiSave } from 'react-icons/fi'
import { toaster } from '@/components/ui/toaster'
import { ScriptConfigForm, toSaveAlternates, type ScriptConfigValue } from '../../ScriptConfigForm'
import { Card } from '../../../shared'
import { useFollowUpScripts, useSaveFollowUpScript } from '../../../../hooks/useFollowUpCalls'
import { extractApiMessage } from '../../../../lib/apiError'
import type { FollowUpKind, FollowUpMode } from '../../../../types/followUp'

export interface FollowUpScriptEditorProps {
  campaignId: string
  kind: FollowUpKind
  customKindId?: string | null
  /** Only needed for custom kinds: Call -> "Script for these calls", Route -> "Follow-up script". */
  mode?: FollowUpMode
}

/** One kind's follow-up script: a default plus priority-ordered alternates (the same editor the
 *  campaign script uses), saved per campaign + kind. */
export function FollowUpScriptEditor({ campaignId, kind, customKindId = null, mode }: FollowUpScriptEditorProps) {
  const { data: configs, isLoading } = useFollowUpScripts(campaignId)
  const { mutateAsync: saveScript, isPending } = useSaveFollowUpScript(campaignId)

  const [value, setValue] = useState<ScriptConfigValue>({ defaultScriptId: null, alternates: [] })
  const [initialized, setInitialized] = useState(false)

  const config = configs?.find(c => c.kind === kind && (c.customKindId ?? null) === (customKindId ?? null))

  // Seed once from the server. A kind with no saved row yet starts empty.
  useEffect(() => {
    if (!configs || initialized) return
    setValue({
      defaultScriptId: config?.defaultScriptId ?? null,
      alternates: (config?.alternates ?? []).map(a => ({
        scriptId: a.scriptId,
        conditionType: a.conditionType,
        conditionTagId: a.conditionTagId,
        conditionTurfMapId: a.conditionTurfMapId,
        priority: a.priority,
      })),
    })
    setInitialized(true)
  }, [configs, config, initialized])

  const isCallScript = kind === 'CallBack' || mode === 'Call'
  const heading = isCallScript ? 'Script for these calls' : 'Follow-up script'

  const handleSave = async () => {
    try {
      await saveScript({
        kind,
        customKindId,
        defaultScriptId: value.defaultScriptId,
        alternates: toSaveAlternates(value.alternates),
      })
      toaster.create({ title: 'Script saved', type: 'success', duration: 3000 })
    } catch (err) {
      toaster.create({
        title: 'Could not save the script',
        description: extractApiMessage(err, 'Please try again.'),
        type: 'error',
        duration: 5000,
      })
    }
  }

  return (
    <Card p={{ base: 4, md: 5 }} w="full" minW={0} data-testid="follow-up-script-editor">
      <HStack justify="space-between" mb={3} gap={2}>
        <Heading size="sm">{heading}</Heading>
        <Button size="sm" colorPalette="brand" minH="44px" loading={isPending} disabled={isLoading || !initialized} onClick={handleSave}>
          <FiSave />Save script
        </Button>
      </HStack>
      {isLoading ? (
        <VStack py={4}><Spinner color="accent" /></VStack>
      ) : (
        <>
          <Text fontSize="xs" color="muted" mb={3}>
            {isCallScript
              ? 'Callers see this script on each call. Alternates apply first when their condition matches the person.'
              : 'Shown for these requests. Alternates apply first when their condition matches the person.'}
          </Text>
          <ScriptConfigForm campaignId={campaignId} value={value} onChange={setValue} />
        </>
      )}
    </Card>
  )
}
